import { useCallback, useMemo, useRef, useState } from 'react';

import {
  STEP_SELECTOR_SEED_CLEAR_VARIETY_LABEL,
  STEP_SELECTOR_SEED_DECIDE_LATER_LABEL,
} from 'constants/uiConstants';
import { GcVariety } from 'interfaces/IGlobalCatalogue';

import { ModifierEvent, PickerFlyoutHost } from './pickerFlyoutRules';
import {
  composeSeedVarietySelection,
  filterVarietiesByName,
  matchesByIdOrName,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';

const EMPTY_VARIETIES: GcVariety[] = [];

interface SeedVarietyFlyoutArgs extends PickerFlyoutHost {
  isSeedTab: boolean;
  initialCropGlobalId?: string | null;
  initialCropName?: string | null;
  initialVarietyGlobalId?: string | null;
  initialVarietyName?: string | null;
}

const findVariety = (
  crop: StepSelectorOption,
  varietyId: string | null | undefined,
  varietyName: string | null | undefined
): GcVariety | null =>
  crop.varieties?.find((variety) =>
    matchesByIdOrName(
      { id: variety.varietyId, name: variety.varietyName },
      { id: varietyId, name: varietyName }
    )
  ) ?? null;

/**
 * Seed tab: crop rows and the variety flyout.
 * - Single-select: a crop click opens the flyout; picking a variety or Pick Variety Later commits.
 * - Multi-select: a crop click checks it and opens the flyout (or unchecks a checked crop);
 *   picks are staged. Change Variety reopens the flyout on the staged variety.
 * - Edit mode pre-selects the step's crop and variety once per session.
 */
const useSeedVarietyFlyout = ({
  filteredOptions,
  highlightedItem,
  setHighlightedItem,
  setIsFlyoutOpen,
  selections,
  rules,
  commitSingleSelect,
  isSeedTab,
  initialCropGlobalId,
  initialCropName,
  initialVarietyGlobalId,
  initialVarietyName,
}: SeedVarietyFlyoutArgs) => {
  const [highlightedVariety, setHighlightedVariety] =
    useState<GcVariety | null>(null);
  const [varietySearchText, setVarietySearchText] = useState('');
  const preselectionAppliedRef = useRef(false);

  const varieties = highlightedItem?.varieties ?? EMPTY_VARIETIES;
  const filteredVarieties = useMemo(
    () => filterVarietiesByName(varieties, varietySearchText),
    [varieties, varietySearchText]
  );

  /**
   * Edit pre-selection: highlights the step's crop and variety and opens the two-panel, once per
   * session. StepInputSelector calls it from an effect after its open reset, which would undo it.
   */
  const applyEditPreselection = useCallback(() => {
    if (!initialCropGlobalId || !isSeedTab) return;
    if (preselectionAppliedRef.current || filteredOptions.length === 0) return;
    const crop = filteredOptions.find((option) =>
      matchesByIdOrName(
        { id: option.globalId, name: option.item },
        { id: initialCropGlobalId, name: initialCropName }
      )
    );
    if (!crop) return;

    preselectionAppliedRef.current = true;
    setHighlightedItem(crop);
    setIsFlyoutOpen(true);
    if (initialVarietyGlobalId) {
      setHighlightedVariety(
        findVariety(crop, initialVarietyGlobalId, initialVarietyName)
      );
    }
  }, [
    filteredOptions,
    initialCropGlobalId,
    initialCropName,
    initialVarietyGlobalId,
    initialVarietyName,
    isSeedTab,
    setHighlightedItem,
    setIsFlyoutOpen,
  ]);

  const getStagedCrop = (crop: StepSelectorOption) =>
    selections.checkedItems.find((checked) => stepOptionsMatch(checked, crop));

  // The variety to show as highlighted: the staged one in multi-select, else the edited step's.
  const findExistingVarietyForCrop = (
    crop: StepSelectorOption
  ): GcVariety | null => {
    if (selections.isMultiSelectMode) {
      const staged = getStagedCrop(crop);
      return staged?.globalId2 || staged?.item2
        ? findVariety(crop, staged.globalId2, staged.item2)
        : null;
    }
    return crop.globalId === initialCropGlobalId && initialVarietyGlobalId
      ? findVariety(crop, initialVarietyGlobalId, initialVarietyName)
      : null;
  };

  const close = useCallback(() => {
    setIsFlyoutOpen(false);
    setHighlightedVariety(null);
    setVarietySearchText('');
  }, [setIsFlyoutOpen]);

  const reset = useCallback(() => {
    setHighlightedVariety(null);
    setVarietySearchText('');
  }, []);

  const resetPreselection = useCallback(() => {
    preselectionAppliedRef.current = false;
  }, []);

  const openForCrop = (crop: StepSelectorOption) => {
    setHighlightedItem(crop);
    setIsFlyoutOpen(true);
    setHighlightedVariety(findExistingVarietyForCrop(crop));
    setVarietySearchText('');
  };

  const handleCropClick = (crop: StepSelectorOption, event?: ModifierEvent) => {
    if (!rules.isMultiSelectRowClick(event)) {
      openForCrop(crop);
      return;
    }
    selections.lockMultiSelect();
    if (selections.isCheckedInActiveCategory(crop)) {
      rules.uncheckRow(crop, close);
      return;
    }
    selections.toggleCheckedItem(crop);
    openForCrop(crop);
  };

  // Stages in multi-select (or on Ctrl/Cmd), otherwise commits straight away.
  const stageOrCommit = (
    selection: StepSelectorOption,
    event?: ModifierEvent
  ) => {
    close();
    if (rules.isMultiSelectFlyoutClick(event)) {
      selections.lockMultiSelect();
      selections.upsertCheckedItem(selection);
    } else {
      commitSingleSelect(selection);
    }
  };

  const handleVarietySelect = (variety: GcVariety, event?: ModifierEvent) => {
    if (!highlightedItem) return;
    stageOrCommit(composeSeedVarietySelection(highlightedItem, variety), event);
  };

  const handleConfirmVariety = (event?: ModifierEvent) => {
    if (highlightedVariety) handleVarietySelect(highlightedVariety, event);
  };

  const handleDecideLater = (event?: ModifierEvent) => {
    if (highlightedItem) stageOrCommit(highlightedItem, event);
  };

  const hasExistingVariety = selections.isMultiSelectMode
    ? !!(
        highlightedItem &&
        (getStagedCrop(highlightedItem)?.globalId2 ||
          getStagedCrop(highlightedItem)?.item2)
      )
    : !!(
        highlightedItem &&
        initialCropGlobalId &&
        highlightedItem.globalId === initialCropGlobalId &&
        initialVarietyGlobalId
      );

  return {
    highlightedVariety,
    setHighlightedVariety,
    varietySearchText,
    setVarietySearchText,
    filteredVarieties,
    decideLaterLabel: hasExistingVariety
      ? STEP_SELECTOR_SEED_CLEAR_VARIETY_LABEL
      : STEP_SELECTOR_SEED_DECIDE_LATER_LABEL,
    close,
    reset,
    resetPreselection,
    applyEditPreselection,
    openForCrop,
    handleCropClick,
    handleVarietySelect,
    handleConfirmVariety,
    handleDecideLater,
  };
};

export default useSeedVarietyFlyout;
